import { useCallback, useState } from 'react';

const DEFAULT_MIN_W = 400;
const DEFAULT_MIN_H = 280;
const MAX_VIEWPORT_RATIO = 0.9;

export interface OpsGridPopupSize {
  width: number;
  height: number;
}

export interface OpsGridPopupSizeLimits {
  minWidth?: number;
  minHeight?: number;
}

/** localStorage key for a grid row detail (view) popup size. */
export function opsGridDetailPopupSizeKey(gridStateStorageKey: string): string {
  return `${gridStateStorageKey}:detail-popup-size`;
}

/** localStorage key for a grid edit/create form popup size. */
export function opsGridFormPopupSizeKey(gridStateStorageKey: string): string {
  return `${gridStateStorageKey}:form-popup-size`;
}

function clampPopupDimensions(
  width: number,
  height: number,
  limits?: OpsGridPopupSizeLimits
): OpsGridPopupSize {
  const minW = limits?.minWidth ?? DEFAULT_MIN_W;
  const minH = limits?.minHeight ?? DEFAULT_MIN_H;
  if (typeof window === 'undefined') {
    return {
      width: Math.max(minW, Math.round(width)),
      height: Math.max(minH, Math.round(height))
    };
  }
  const maxW = Math.floor(window.innerWidth * MAX_VIEWPORT_RATIO);
  const maxH = Math.floor(window.innerHeight * MAX_VIEWPORT_RATIO);
  return {
    width: Math.min(Math.max(minW, Math.round(width)), maxW),
    height: Math.min(Math.max(minH, Math.round(height)), maxH)
  };
}

function readStoredSize(storageKey: string, limits?: OpsGridPopupSizeLimits): OpsGridPopupSize | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { width?: number; height?: number };
    if (parsed.width == null || parsed.height == null) return null;
    return clampPopupDimensions(parsed.width, parsed.height, limits);
  } catch {
    return null;
  }
}

function writeStoredSize(storageKey: string, size: OpsGridPopupSize, limits?: OpsGridPopupSizeLimits): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(clampPopupDimensions(size.width, size.height, limits)));
  } catch {
    /* ignore quota */
  }
}

function parsePopupDimension(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = parseInt(value, 10);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

/** Persisted width/height for a resizable site grid popup (one key per grid/popup). */
export function useOpsGridPopupSize(
  defaultWidth: number,
  storageKey: string,
  limits?: OpsGridPopupSizeLimits
) {
  const [userSize, setUserSize] = useState<OpsGridPopupSize | null>(() => readStoredSize(storageKey, limits));

  const setAndStoreSize = useCallback(
    (next: OpsGridPopupSize) => {
      const clamped = clampPopupDimensions(next.width, next.height, limits);
      setUserSize(clamped);
      writeStoredSize(storageKey, clamped, limits);
    },
    [limits, storageKey]
  );

  const onResizeEnd = useCallback(
    (e: { component?: { option: (name: string) => unknown } }) => {
      const width = parsePopupDimension(e.component?.option('width'));
      const height = parsePopupDimension(e.component?.option('height'));
      if (width === undefined || height === undefined) return;
      setAndStoreSize({ width, height });
    },
    [setAndStoreSize]
  );

  const hasUserSize = userSize !== null;
  const maxWidth = typeof window === 'undefined' ? defaultWidth : Math.floor(window.innerWidth * MAX_VIEWPORT_RATIO);
  const maxHeight =
    typeof window === 'undefined' ? DEFAULT_MIN_H : Math.floor(window.innerHeight * MAX_VIEWPORT_RATIO);
  const isMaximized =
    userSize !== null &&
    Math.abs(userSize.width - maxWidth) <= 2 &&
    Math.abs(userSize.height - maxHeight) <= 2;

  const maximize = useCallback(() => {
    setAndStoreSize({ width: maxWidth, height: maxHeight });
  }, [maxHeight, maxWidth, setAndStoreSize]);

  const restoreDefault = useCallback(() => {
    setUserSize(null);
    try {
      localStorage.removeItem(storageKey);
    } catch {
      /* ignore storage access */
    }
  }, [storageKey]);

  return {
    width: userSize?.width ?? defaultWidth,
    height: userSize?.height,
    hasUserSize,
    isMaximized,
    maximize,
    restoreDefault,
    onResizeEnd
  };
}
